import type { ToolResult } from './messageContent'
import type { Editor } from './editorBridge'
import { auth } from './firebase'

const BASE = import.meta.env.VITE_API_URL ?? ''

export type VideoAdClip = { url: string; caption: string; seconds: number }
export type VideoAd = { aspect_ratio: string; clips: VideoAdClip[] }

export function videoAdOf(result: ToolResult | undefined): VideoAd | null {
  if (!result || result.isError) return null
  try {
    const parsed = JSON.parse(result.content) as { video_ad?: VideoAd }
    return parsed.video_ad?.clips?.length ? parsed.video_ad : null
  } catch {
    return null
  }
}

export async function mediaBytes(brandId: number, url: string, token?: string) {
  const query = new URLSearchParams({ brand_id: String(brandId), url })
  const bearer = token ?? (await auth.currentUser?.getIdToken())
  const response = await fetch(`${BASE}/chatbot/media?${query}`, {
    headers: bearer ? { Authorization: `Bearer ${bearer}` } : {},
  })
  if (!response.ok) throw new Error(`Couldn't load that file (${response.status}).`)
  return { bytes: await response.arrayBuffer(), type: response.headers.get('content-type') ?? '' }
}

export function fileName(url: string) {
  return url.split('?')[0].split('/').pop() || 'media'
}

async function withBytes(clips: VideoAdClip[], brandId: number) {
  const token = await auth.currentUser?.getIdToken()
  return Promise.all(
    clips.map(async (clip) => ({
      ...clip,
      name: fileName(clip.url),
      bytes: (await mediaBytes(brandId, clip.url, token)).bytes,
    })),
  )
}

export async function appendToEditor(editor: Editor, clips: VideoAdClip[], brandId: number) {
  const loaded = await withBytes(clips, brandId)
  const { paths } = (await editor.call(
    'adlovin:append',
    { clips: loaded },
    loaded.map((clip) => clip.bytes),
  )) as { paths: string[] }
  return paths.map((path, index) => ({ path, url: clips[index].url }))
}

export async function loadIntoEditor(editor: Editor, ad: VideoAd, brandId: number, name: string) {
  const clips = await withBytes(ad.clips, brandId)
  const { paths } = (await editor.call(
    'adlovin:open',
    { ad: { name, aspect_ratio: ad.aspect_ratio, clips } },
    clips.map((clip) => clip.bytes),
  )) as { paths: string[] }
  return paths.map((path, index) => ({ path, url: ad.clips[index].url }))
}
